import { useEffect, useRef, type RefObject } from 'react';
import { smoothScrollIntoView } from '@/hooks/smooth-scroll-into-view';

interface UseScrollIntoViewOnRevealOptions {
  enabled?: boolean;
  block?: ScrollLogicalPosition;
  behavior?: ScrollBehavior;
  focus?: boolean;
}

/**
 * Scrolls a newly revealed element into view once when its visibility toggles on.
 * Useful for progressive mobile forms where the next action appears below the fold.
 */
export function useScrollIntoViewOnReveal(
  targetRef: RefObject<HTMLElement | null>,
  isVisible: boolean,
  {
    enabled = true,
    block = 'center',
    behavior = 'smooth',
    focus = false,
  }: UseScrollIntoViewOnRevealOptions = {},
): void {
  const wasVisibleRef = useRef(isVisible);

  useEffect(() => {
    const wasVisible = wasVisibleRef.current;
    wasVisibleRef.current = isVisible;

    if (!enabled || !isVisible || wasVisible) {
      return;
    }

    const target = targetRef.current;
    if (!target) {
      return;
    }

    let frameId = 0;
    let nestedFrameId = 0;
    let cancelScroll = () => {};

    frameId = window.requestAnimationFrame(() => {
      nestedFrameId = window.requestAnimationFrame(() => {
        cancelScroll = smoothScrollIntoView(target, { behavior, block });
        if (focus) {
          target.focus({ preventScroll: true });
        }
      });
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.cancelAnimationFrame(nestedFrameId);
      cancelScroll();
    };
  }, [behavior, block, enabled, focus, isVisible, targetRef]);
}
